import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { useAccess } from '../auth/AccessContext';
import type {
  Project, SourceTenant, SourceTenantCreate, DiscoveredProject,
  UserRecord, UserInvite, UserProjectAccess,
  EpicSummary, EpicTreeNode, IterationVelocity, WorkItemComment,
  ConfigSetting, ConfigSettingCreate, ConfigSettingUpdate,
  WsjfBacklog, ValueAtRisk, BugAging, BurnupReport, CostVsEstimate, ActivityHeatmap,
  CustomerRoadmap, CustomerProgress, CustomerUpcoming, CustomerDecisionsAndRisks,
  Cfd, SpecQuality, CycleTime,
  ConcurrentTimeline, ProjectDates, SignoffQueue,
} from './types';

// The signed-in user's DorUsers profile (id, organisationId, role, …), used by the layout to
// drive nav visibility (Programme = internal, For Customer = customer-facing, etc.).
//
// Single source of truth: the auth gate's AccessContext resolves a force-refreshed token and
// /api/me ONCE (SPA-Auth Standard, rules 1–3), separating "not provisioned" from transient
// errors. Components under <RootGate> read the already-resolved profile here. The legacy
// `enabled` arg is accepted for source-compat but ignored — the gate owns when resolution runs.
export const useMeProfile = (_options?: { enabled?: boolean }) => {
  const { status, profile } = useAccess();
  return {
    data: profile,
    isLoading: status === 'loading',
    isError: status === 'error',
  } as const;
};

// ── Reports ──
export const useMyProjects = () =>
  useQuery({ queryKey: ['reports', 'projects'], queryFn: () => api.get<Project[]>('/reports/projects') });

export const useEpics = (projectId: string | undefined) =>
  useQuery({
    queryKey: ['reports', 'epics', projectId],
    queryFn: () => api.get<EpicSummary[]>(`/reports/projects/${projectId}/epics`),
    enabled: !!projectId,
  });

export const useEpicTree = (projectId: string | undefined, epicId: number | undefined) =>
  useQuery({
    queryKey: ['reports', 'tree', projectId, epicId],
    queryFn: () => api.get<EpicTreeNode>(`/reports/projects/${projectId}/epics/${epicId}/tree`),
    enabled: !!projectId && !!epicId,
  });

// Discussion comments for a single work item (e.g. a User Story on the Feature
// Progress detail panel). Oldest-first, as returned by the API. Only fetches when
// a story is selected.
export const useWorkItemComments = (projectId: string | undefined, workItemId: number | undefined) =>
  useQuery({
    queryKey: ['reports', 'comments', projectId, workItemId],
    queryFn: () => api.get<WorkItemComment[]>(`/reports/projects/${projectId}/workitems/${workItemId}/comments`),
    enabled: !!projectId && !!workItemId,
    staleTime: 60_000,
  });

export const useVelocity = (projectId: string | undefined, team?: string) =>
  useQuery({
    queryKey: ['reports', 'velocity', projectId, team],
    queryFn: () => api.get<IterationVelocity[]>(`/reports/projects/${projectId}/velocity${team ? `?team=${encodeURIComponent(team)}` : ''}`),
    enabled: !!projectId,
  });

// ── Source Tenants (admin) ──
export const useSourceTenants = () =>
  useQuery({ queryKey: ['admin', 'source-tenants'], queryFn: () => api.get<SourceTenant[]>('/manage/source-tenants') });

export const useCreateSourceTenant = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SourceTenantCreate) => api.post<SourceTenant>('/manage/source-tenants', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'source-tenants'] }),
  });
};

export const useDiscoverProjects = (sourceTenantId: string | undefined) =>
  useQuery({
    queryKey: ['admin', 'discover', sourceTenantId],
    queryFn: () => api.get<DiscoveredProject[]>(`/manage/source-tenants/${sourceTenantId}/discover-projects`),
    enabled: !!sourceTenantId,
  });

export const useRegisterProject = (sourceTenantId: string | undefined) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { devOpsProjectId: string; name: string }) =>
      api.post<Project>(`/manage/source-tenants/${sourceTenantId}/projects`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'discover', sourceTenantId] });
      qc.invalidateQueries({ queryKey: ['admin', 'projects'] });
    },
  });
};

export const useProjects = () =>
  useQuery({ queryKey: ['admin', 'projects'], queryFn: () => api.get<Project[]>('/manage/projects') });

// ── Users (admin) ──
export const useUsers = () =>
  useQuery({ queryKey: ['admin', 'users'], queryFn: () => api.get<UserRecord[]>('/manage/users') });

export const useInviteUser = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UserInvite) => api.post<UserRecord>('/manage/users', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
};

export const useRevokeUser = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/manage/users/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
};

// ── User-Project Access (admin) ──
export const useUserAccess = (userId: string | undefined) =>
  useQuery({
    queryKey: ['admin', 'access', userId],
    queryFn: () => api.get<UserProjectAccess[]>(`/manage/users/${userId}/access`),
    enabled: !!userId,
  });

export const useGrantAccess = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { userId: string; projectId: string }) => api.post('/manage/access', body),
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['admin', 'access', vars.userId] }),
  });
};

export const useRevokeAccess = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { userId: string; projectId: string }) =>
      api.delete(`/manage/access/${vars.userId}/${vars.projectId}`),
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['admin', 'access', vars.userId] }),
  });
};

// ── Central Config (SystemAdmin only) ──
// Query-key factory so future invalidations stay consistent (STATE-002).
export const configKeys = {
  all: ['admin', 'config'] as const,
  list: () => [...configKeys.all, 'list'] as const,
};

export const useConfigSettings = () =>
  useQuery({ queryKey: configKeys.list(), queryFn: () => api.get<ConfigSetting[]>('/manage/config') });

export const useCreateConfigSetting = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ConfigSettingCreate) => api.post<ConfigSetting>('/manage/config', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: configKeys.list() }),
  });
};

export const useUpdateConfigSetting = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { key: string; body: ConfigSettingUpdate }) =>
      api.put<ConfigSetting>(`/manage/config/${encodeURIComponent(vars.key)}`, vars.body),
    onSuccess: () => qc.invalidateQueries({ queryKey: configKeys.list() }),
  });
};

// ── Programme reports (internal) ──
export const programmeKeys = {
  all: ['reports', 'programme'] as const,
  wsjf: (id?: string) => [...programmeKeys.all, 'wsjf', id] as const,
  valueAtRisk: (id?: string) => [...programmeKeys.all, 'value-at-risk', id] as const,
  bugAging: (id?: string) => [...programmeKeys.all, 'bug-aging', id] as const,
  burnup: (id?: string) => [...programmeKeys.all, 'burnup', id] as const,
  costVsEstimate: (id?: string) => [...programmeKeys.all, 'cost-vs-estimate', id] as const,
  activity: (id?: string, days?: number) => [...programmeKeys.all, 'activity', id, days] as const,
};

export const useWsjfBacklog = (projectId: string | undefined) =>
  useQuery({
    queryKey: programmeKeys.wsjf(projectId),
    queryFn: () => api.get<WsjfBacklog>(`/reports/programme/wsjf/${projectId}`),
    enabled: !!projectId,
  });

export const useValueAtRisk = (projectId: string | undefined) =>
  useQuery({
    queryKey: programmeKeys.valueAtRisk(projectId),
    queryFn: () => api.get<ValueAtRisk>(`/reports/programme/value-at-risk/${projectId}`),
    enabled: !!projectId,
  });

export const useBugAging = (projectId: string | undefined) =>
  useQuery({
    queryKey: programmeKeys.bugAging(projectId),
    queryFn: () => api.get<BugAging>(`/reports/programme/bug-aging/${projectId}`),
    enabled: !!projectId,
  });

export const useBurnup = (projectId: string | undefined) =>
  useQuery({
    queryKey: programmeKeys.burnup(projectId),
    queryFn: () => api.get<BurnupReport>(`/reports/programme/burnup/${projectId}`),
    enabled: !!projectId,
  });

export const useCostVsEstimate = (projectId: string | undefined) =>
  useQuery({
    queryKey: programmeKeys.costVsEstimate(projectId),
    queryFn: () => api.get<CostVsEstimate>(`/reports/programme/cost-vs-estimate/${projectId}`),
    enabled: !!projectId,
  });

export const useActivityHeatmap = (projectId: string | undefined, days = 90) =>
  useQuery({
    queryKey: programmeKeys.activity(projectId, days),
    queryFn: () => api.get<ActivityHeatmap>(`/reports/programme/activity/${projectId}?days=${days}`),
    enabled: !!projectId,
  });

// ── Customer-facing reports ──
export const customerKeys = {
  all: ['reports', 'customer'] as const,
  roadmap: (id?: string) => [...customerKeys.all, 'roadmap', id] as const,
  progress: (id?: string) => [...customerKeys.all, 'progress', id] as const,
  upcoming: (id?: string, weeks?: number) => [...customerKeys.all, 'upcoming', id, weeks] as const,
  decisionsRisks: (id?: string) => [...customerKeys.all, 'decisions-risks', id] as const,
};

export const useCustomerRoadmap = (projectId: string | undefined) =>
  useQuery({
    queryKey: customerKeys.roadmap(projectId),
    queryFn: () => api.get<CustomerRoadmap>(`/reports/customer/roadmap/${projectId}`),
    enabled: !!projectId,
  });

export const useCustomerProgress = (projectId: string | undefined) =>
  useQuery({
    queryKey: customerKeys.progress(projectId),
    queryFn: () => api.get<CustomerProgress>(`/reports/customer/progress/${projectId}`),
    enabled: !!projectId,
  });

export const useCustomerUpcoming = (projectId: string | undefined, weeks = 6) =>
  useQuery({
    queryKey: customerKeys.upcoming(projectId, weeks),
    queryFn: () => api.get<CustomerUpcoming>(`/reports/customer/upcoming/${projectId}?weeks=${weeks}`),
    enabled: !!projectId,
  });

export const useCustomerDecisionsAndRisks = (projectId: string | undefined) =>
  useQuery({
    queryKey: customerKeys.decisionsRisks(projectId),
    queryFn: () => api.get<CustomerDecisionsAndRisks>(`/reports/customer/decisions-risks/${projectId}`),
    enabled: !!projectId,
  });

// ── Wave 4 (could-have) ──
export const useCfd = (projectId: string | undefined, days = 90) =>
  useQuery({
    queryKey: [...programmeKeys.all, 'cfd', projectId, days] as const,
    queryFn: () => api.get<Cfd>(`/reports/programme/cfd/${projectId}?days=${days}`),
    enabled: !!projectId,
  });

export const useSpecQuality = (projectId: string | undefined) =>
  useQuery({
    queryKey: [...programmeKeys.all, 'spec-quality', projectId] as const,
    queryFn: () => api.get<SpecQuality>(`/reports/programme/spec-quality/${projectId}`),
    enabled: !!projectId,
  });

export const useCycleTime = (projectId: string | undefined, days = 90) =>
  useQuery({
    queryKey: [...programmeKeys.all, 'cycle-time', projectId, days] as const,
    queryFn: () => api.get<CycleTime>(`/reports/programme/cycle-time/${projectId}?days=${days}`),
    enabled: !!projectId,
  });

// ── FEAT-016 Concurrent Projects Timeline ──
export const useConcurrentTimeline = () =>
  useQuery({
    queryKey: [...programmeKeys.all, 'concurrent-projects'] as const,
    queryFn: () => api.get<ConcurrentTimeline>('/reports/programme/concurrent-projects'),
  });

// Project planned-dates editor (admin)
export const useSetProjectDates = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; body: ProjectDates }) =>
      api.put<Project>(`/manage/projects/${vars.id}/dates`, vars.body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'projects'] });
      qc.invalidateQueries({ queryKey: [...programmeKeys.all, 'concurrent-projects'] });
      qc.invalidateQueries({ queryKey: ['reports', 'projects'] });
    },
  });
};

// ── FEAT-028 Sign-off Queue ──
export const useSignoffQueue = (projectId: string | undefined) =>
  useQuery({
    queryKey: [...customerKeys.all, 'signoff', projectId] as const,
    queryFn: () => api.get<SignoffQueue>(`/reports/customer/signoff-queue/${projectId}`),
    enabled: !!projectId,
  });
